import { useEffect, useState } from 'react';

interface ConfettiEffectProps {
  intensity?: 'light' | 'medium' | 'heavy';
}

interface Confetti {
  id: number;
  left: number;
  width: number;
  height: number;
  animationDuration: number;
  animationDelay: number;
  color: string;
  rotation: number;
}

const ConfettiEffect = ({ intensity = 'medium' }: ConfettiEffectProps) => {
  const [confetti, setConfetti] = useState<Confetti[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  const confettiCounts = {
    light: 40,
    medium: 80,
    heavy: 150,
  };

  const confettiColors = [
    '#FF6B6B',
    '#FF8E53',
    '#FFE66D',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#DDA0DD',
    '#FF69B4',
    '#9B59B6',
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const baseCount = confettiCounts[intensity];
    const count = isMobile ? Math.floor(baseCount * 0.5) : baseCount;
    const newConfetti: Confetti[] = [];

    for (let i = 0; i < count; i++) {
      const isRectangle = Math.random() > 0.5;
      newConfetti.push({
        id: i,
        left: Math.random() * 100,
        width: isRectangle ? 3 : 5,
        height: isRectangle ? 10 : 5,
        animationDuration: Math.random() * 3 + 3,
        animationDelay: Math.random() * 8,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
        rotation: Math.random() * 360,
      });
    }

    setConfetti(newConfetti);
  }, [intensity, isMobile]);

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-40 overflow-hidden"
      style={{ height: '100dvh', minHeight: '100vh' }}
    >
      <style>{`
        @keyframes fall-confetti {
          0% {
            transform: translateY(-10dvh) rotate(0deg) translateX(0) translateZ(0);
            opacity: 1;
          }
          20% {
            transform: translateY(18dvh) rotate(144deg) translateX(15px) translateZ(0);
          }
          40% {
            transform: translateY(36dvh) rotate(288deg) translateX(-10px) translateZ(0);
          }
          60% {
            transform: translateY(54dvh) rotate(432deg) translateX(20px) translateZ(0);
          }
          80% {
            transform: translateY(72dvh) rotate(576deg) translateX(-15px) translateZ(0);
          }
          100% {
            transform: translateY(100dvh) rotate(720deg) translateX(10px) translateZ(0);
            opacity: 0.5;
          }
        }
        
        .confetti-piece {
          position: absolute;
          pointer-events: none;
          animation: fall-confetti linear infinite;
          border-radius: 1px;
          will-change: transform, opacity;
          backface-visibility: hidden;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .confetti-piece {
            animation: none !important;
            opacity: 0 !important;
          }
        }
      `}</style>
      
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: `${piece.left}%`,
            width: `${piece.width}px`,
            height: `${piece.height}px`,
            backgroundColor: piece.color,
            animationDuration: `${piece.animationDuration}s`,
            animationDelay: `${piece.animationDelay}s`,
            transform: `rotate(${piece.rotation}deg)`,
          }}
        />
      ))}
    </div>
  );
};

export default ConfettiEffect;
